import type { Event } from '@sentry/react'

// Only schemes which identify extension-owned resources, not ordinary third-party scripts.
// Safari's schemes are also recognized by the installed Sentry stack parser.
const EXTENSION_PROTOCOLS = new Set([
    'chrome-extension:',
    'moz-extension:',
    'safari-extension:',
    'safari-web-extension:',
])

function extensionSource(filename: string | undefined): string | null {
    if (!filename) return null

    try {
        const url = new URL(filename)
        if (!EXTENSION_PROTOCOLS.has(url.protocol) || !url.hostname || url.username || url.password) return null

        // URL.origin is "null" for these schemes; keep the extension identity instead.
        return `${url.protocol}//${url.hostname}`
    } catch {
        return null
    }
}

export function isExternalNoise(event: Event): boolean {
    if (event.type || event.exception?.values?.length !== 1) return false

    const exception = event.exception.values[0]
    const stack = exception.stacktrace
    const frames = stack?.frames

    // A synthetic, incomplete or linked stack cannot establish exclusive external ownership.
    if (
        exception.mechanism?.synthetic ||
        exception.mechanism?.is_exception_group ||
        stack?.frames_omitted?.some((count) => count > 0) ||
        !frames?.length
    ) {
        return false
    }

    // Sentry stores frames from oldest to newest. Preserve mixed/unknown stacks,
    // even when their innermost frame is external; an app caller may need fixing.
    const origin = frames[frames.length - 1].filename

    if (
        exception.type === 'SyntaxError' &&
        exception.value === 'Unexpected end of input' &&
        origin === 'iabjs://browser_declutter' &&
        frames.every((frame) => frame.filename === origin)
    ) {
        return true
    }

    if (
        exception.type === 'Error' &&
        exception.value === 'Error invoking postMessage: Java bridge method invocation error' &&
        origin === 'iabjs://navigation_performance_logger_android' &&
        frames.every((frame) => frame.filename === origin)
    ) {
        return true
    }

    const extension = extensionSource(origin)
    return extension !== null && frames.every((frame) => extensionSource(frame.filename) === extension)
}
